'use client';

import * as React from 'react';
import { Upload } from 'lucide-react';

import { cn } from '../../lib/utils';

/**
 * File drop zone without a dependency: native drag-and-drop plus a hidden
 * `<input type="file">` opened by click, Enter or Space. The hook is ported
 * from glp-frontend `components/helpers/use-dropzone.tsx`; the look (dashed,
 * rounded, file name in place of the prompt) from the presentations
 * `LogoDropZone`s. Files outside `accept` go to `onReject` instead of
 * `onFiles`; any conversion (HEIC → JPG, size limits) is the caller's job.
 */

/** A MIME pattern (`image/*`, comma list) or react-dropzone's `{ mime: ['.ext'] }`. */
export type FileAccept = string | Record<string, string[]>;

const extensionOf = (name: string) => {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot).toLowerCase();
};

const mimeMatches = (type: string, pattern: string) => {
  const p = pattern.trim().toLowerCase();
  if (!p) return false;
  if (p.startsWith('.')) return false;
  if (p.endsWith('/*')) return type.toLowerCase().startsWith(p.slice(0, -1));
  return type.toLowerCase() === p;
};

/** Whether `file` passes `accept`. Falls back to the extension when the browser gives no type. */
export function fileMatchesAccept(file: File, accept: FileAccept | undefined): boolean {
  if (!accept) return true;
  const ext = extensionOf(file.name);
  if (typeof accept === 'string') {
    return accept
      .split(',')
      .some((part) =>
        part.trim().startsWith('.')
          ? part.trim().toLowerCase() === ext
          : file.type
            ? mimeMatches(file.type, part)
            : false,
      );
  }
  return Object.entries(accept).some(
    ([mime, extensions]) =>
      (file.type && mimeMatches(file.type, mime)) ||
      extensions.some((candidate) => candidate.toLowerCase() === ext),
  );
}

/** The `accept` attribute for the hidden input. */
export function acceptAttribute(accept: FileAccept | undefined): string | undefined {
  if (!accept) return undefined;
  if (typeof accept === 'string') return accept;
  return Object.entries(accept)
    .flatMap(([mime, extensions]) => [mime, ...extensions])
    .join(',');
}

export interface UseFileDropzoneOptions {
  accept?: FileAccept;
  multiple?: boolean;
  disabled?: boolean;
  onFiles: (files: File[]) => void | Promise<void>;
  onReject?: (files: File[]) => void;
}

export function useFileDropzone({
  accept,
  multiple = false,
  disabled = false,
  onFiles,
  onReject,
}: UseFileDropzoneOptions) {
  const [isDragActive, setDragActive] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const depth = React.useRef(0);

  const handleFiles = React.useCallback(
    async (list: FileList | File[] | null | undefined) => {
      const files = Array.from(list ?? []);
      if (files.length === 0) return;
      const accepted = files.filter((file) => fileMatchesAccept(file, accept));
      const rejected = files.filter((file) => !fileMatchesAccept(file, accept));
      if (rejected.length > 0) onReject?.(rejected);
      if (accepted.length > 0) await onFiles(multiple ? accepted : accepted.slice(0, 1));
    },
    [accept, multiple, onFiles, onReject],
  );

  const open = React.useCallback(() => {
    if (!disabled) inputRef.current?.click();
  }, [disabled]);

  const rootProps = {
    onDragEnter: (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
      if (disabled) return;
      depth.current += 1;
      setDragActive(true);
    },
    onDragOver: (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
    },
    onDragLeave: (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setDragActive(false);
    },
    onDrop: (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
      depth.current = 0;
      setDragActive(false);
      if (disabled) return;
      return handleFiles(event.dataTransfer?.files);
    },
    onClick: open,
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        open();
      }
    },
  };

  const inputProps = {
    ref: inputRef,
    type: 'file' as const,
    accept: acceptAttribute(accept),
    multiple,
    disabled,
    tabIndex: -1,
    className: 'sr-only',
    onClick: (event: React.MouseEvent<HTMLInputElement>) => event.stopPropagation(),
    onChange: async (event: React.ChangeEvent<HTMLInputElement>) => {
      const input = event.currentTarget;
      await handleFiles(event.target.files);
      // Same file twice in a row still fires change.
      if (input) input.value = '';
    },
  };

  return { isDragActive, inputRef, open, rootProps, inputProps };
}

export interface FileDropzoneProps
  extends UseFileDropzoneOptions,
    Omit<React.HTMLAttributes<HTMLDivElement>, 'onDrop' | 'children'> {
  /** The chosen file's name; replaces the prompt. */
  fileName?: string | null;
  placeholder?: React.ReactNode;
  activeText?: React.ReactNode;
  /** Red border, e.g. from a form error. */
  invalid?: boolean;
  /** Custom content; replaces the prompt/file name entirely. */
  children?: React.ReactNode;
  /** Id for the hidden input, so a `<Label htmlFor>` can point at it. */
  inputId?: string;
}

const FileDropzone = React.forwardRef<HTMLDivElement, FileDropzoneProps>(
  (
    {
      accept,
      multiple,
      disabled,
      onFiles,
      onReject,
      fileName,
      placeholder = 'Drag and drop or click to select a file',
      activeText = 'Drop the file here…',
      invalid,
      className,
      children,
      inputId,
      ...props
    },
    ref,
  ) => {
    const { isDragActive, rootProps, inputProps } = useFileDropzone({
      accept,
      multiple,
      disabled,
      onFiles,
      onReject,
    });
    return (
      <div
        ref={ref}
        role='button'
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        data-drag-active={isDragActive || undefined}
        {...rootProps}
        {...props}
        className={cn(
          'flex min-h-16 w-full cursor-pointer flex-col items-center justify-center gap-1 rounded-3xl border-2 border-dashed border-gray-300 bg-white px-4 py-3 text-center text-xs text-grey-text-color transition-colors hover:border-global-green/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-global-green/40',
          isDragActive && 'border-global-green bg-light-global-green',
          invalid && 'border-red-500',
          disabled && 'cursor-not-allowed opacity-50',
          className,
        )}
      >
        <input id={inputId} {...inputProps} />
        {children ??
          (isDragActive ? (
            <span>{activeText}</span>
          ) : fileName ? (
            <span className='max-w-full break-all font-medium text-neutral-800'>{fileName}</span>
          ) : (
            <span className='flex items-center gap-2'>
              <Upload aria-hidden='true' className='h-4 w-4 shrink-0' />
              {placeholder}
            </span>
          ))}
      </div>
    );
  },
);
FileDropzone.displayName = 'FileDropzone';

export { FileDropzone };
